import AuthLayout from "./AuthLayout";
import { useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import OTPInput from "react-otp-input";
import { useState, useEffect } from "react";
import { useVerifyForgotPasswordOtp, useSendForgotPasswordOtp } from "../../api/hooks/useAuth";
import { showSuccess, showError } from "../../helpers/toast";
import { load, save, remove } from "../../helpers/storage";
import { STORAGE_KEYS } from "../../constants/storage";
import { useAuthActions } from "../../state/hooks";
import { OTP_LENGTH, RESEND_TIMER_SECONDS } from "../../constants/otp";

const OtpVerification = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [resendTimer, setResendTimer] = useState(RESEND_TIMER_SECONDS);
  const { setToken } = useAuthActions();

  useEffect(() => {
    // Load email from storage
    const loadEmail = async () => {
      const credentials = await load<{ email: string }>(STORAGE_KEYS.credentials);
      if (credentials?.email) {
        setEmail(credentials.email);
      } else {
        // If no email found, redirect to forgot password
        showError("Email not found. Please start again.");
        navigate("/forgot-password");
      }
    };
    loadEmail();
  }, [navigate]);

  useEffect(() => {
    // Resend timer
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const { mutate: verifyOtp, isPending: isVerifying } = useVerifyForgotPasswordOtp({
    onSuccess: (response) => {
      showSuccess(response.message || "OTP verified successfully");
      
      // Store token if provided
      const token = response.data?.token;
      if (token) {
        setToken(token);
        save(STORAGE_KEYS.token, token);
      }
      
      // Navigate to reset password
      navigate("/reset-password");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Invalid OTP. Please try again.";
      showError(errorMessage);
      setOtp(""); // Clear OTP on error
    },
  });

  const { mutate: resendOtp, isPending: isResending } = useSendForgotPasswordOtp({
    onSuccess: (response) => {
      showSuccess(response.message || "OTP resent successfully");
      setResendTimer(RESEND_TIMER_SECONDS); // Reset timer
      setOtp(""); // Clear current OTP
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to resend OTP. Please try again.";
      showError(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== OTP_LENGTH) {
      showError(`Please enter a valid ${OTP_LENGTH}-digit OTP`);
      return;
    }
    if (!email) {
      showError("Email not found. Please start again.");
      navigate("/forgot-password");
      return;
    }
    verifyOtp({ email, otp });
  };

  const handleResend = () => {
    if (resendTimer > 0 || isResending) return;
    if (!email) {
      showError("Email not found. Please start again.");
      navigate("/forgot-password");
      return;
    }
    resendOtp(email);
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Verify Email Address</h2>
        <p>
          A verification OTP has been sent to your email. Please check your
          inbox.
        </p>
        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <label htmlFor="otp">Enter OTP</label>
            <div className="opt_fields">
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={OTP_LENGTH}
                renderInput={(props) => <input {...props} id="otp" />}
                inputType="tel"
                shouldAutoFocus
              />
            </div>
            <div className="auth_bottom">
              <p>
                <button
                  type="button"
                  className="link"
                  onClick={handleResend}
                  disabled={resendTimer > 0 || isResending}
                >
                  {isResending
                    ? "Resending..."
                    : resendTimer > 0
                    ? `Resend OTP (${resendTimer}s)`
                    : "Resend OTP"}
                </button>
              </p>
            </div>
          </div>
          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              variant="contained"
              disabled={isVerifying || otp.length !== OTP_LENGTH}
            >
              {isVerifying ? "Verifying..." : "Submit"}
            </Button>
          </div>
        </form>
        <div className="auth_bottom">
          <p>
            <button
              type="button"
              className="link"
              onClick={() => {
                remove(STORAGE_KEYS.credentials);
                navigate("/");
              }}
              disabled={isVerifying}
            >
              <KeyboardBackspaceIcon /> Back to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default OtpVerification;
